import { Box, Button, Dialog, Paper, Typography } from "@mui/material";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR, isReportableStatus } from "../constants";

interface ReportLeadsModalProps {
  data: LeadsManagementData;
}

export default function ReportLeadsModal({ data }: ReportLeadsModalProps) {
  const { showConfirm, selectedLeads, cancelBulkReport, confirmBulkReport, isReporting, canReportLeads } = data;

  const reportableSelected = selectedLeads.filter((lead) => isReportableStatus(lead.status));
  const brandCounts = new Map<string, number>();
  reportableSelected.forEach((lead) => {
    brandCounts.set(lead.brand, (brandCounts.get(lead.brand) ?? 0) + 1);
  });
  const brandBreakdown = Array.from(brandCounts.entries())
    .map(([brand, count]) => ({ brand, count }))
    .sort((a, b) => b.count - a.count);

  return (
    <Dialog open={showConfirm} onClose={cancelBulkReport} maxWidth="xs" fullWidth>
      <Box sx={{ p: "26px 28px" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a" }}>
          Report leads to brand
        </Typography>
        <Typography sx={{ fontSize: 14.5, color: "#616161", mt: 1.5, mb: 2.25, lineHeight: 1.5 }}>
          You're about to report <strong>{reportableSelected.length}</strong> lead(s) to their brand
          partners. This action cannot be undone.
        </Typography>

        <Paper
          variant="outlined"
          sx={{ bgcolor: "#faf5fc", borderColor: "#f0e3f5", p: "6px 16px", maxHeight: 200, overflowY: "auto" }}
        >
          {brandBreakdown.map(({ brand, count }) => (
            <Box
              key={brand}
              sx={{ display: "flex", alignItems: "center", py: 1.25, borderBottom: "1px solid #f2e7f6" }}
            >
              <Typography sx={{ flex: 1, fontSize: 14, color: "#424242" }}>{brand}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: BRAND_COLOR }}>{count}</Typography>
            </Box>
          ))}
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.75, mt: 2.75 }}>
          <Button
            onClick={cancelBulkReport}
            disabled={isReporting}
            sx={{ color: "#757575", letterSpacing: "0.4px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmBulkReport}
            disabled={!canReportLeads || isReporting || reportableSelected.length === 0}
            data-tracker="930"
            sx={{ bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#7B1FA2" }, letterSpacing: "0.4px" }}
          >
            {isReporting ? "Reporting…" : "Confirm & report"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
